/**
 * Review Uploads page -- moderation of participant uploads
 */

import { useState, useEffect, useRef, type SyntheticEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  Video,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  User,
  Sparkles,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import { getEvent, getUploads, moderateUpload, bulkModerate, aiReviewUploads, reprocessUpload } from "@/lib/api";
import type { MixUpload } from "@/lib/store";
import { cn, formatDuration, formatBytes, formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

type FilterTab = "all" | "pending" | "approved" | "rejected";

export function ReviewUploads() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterTab>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [moderatingId, setModeratingId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const { data: event } = useQuery({
    queryKey: ["event", id],
    queryFn: () => getEvent(id!),
    enabled: !!id,
  });

  const { data: uploadsResponse, isLoading } = useQuery({
    queryKey: ["uploads", id],
    queryFn: () => getUploads(id!, { limit: 200 }),
    enabled: !!id,
    refetchInterval: (query) => {
      const resp = query.state.data;
      if (!resp) return false;
      const hasUnprocessed = resp.data.some((u: MixUpload) => !u.width);
      return hasUnprocessed ? 10_000 : false;
    },
  });
  const uploads = uploadsResponse?.data;

  const moderateMutation = useMutation({
    mutationFn: ({
      uploadId,
      status,
      note,
    }: {
      uploadId: string;
      status: "approved" | "rejected";
      note?: string;
    }) => moderateUpload(id!, uploadId, status, note),
    onMutate: async ({ uploadId, status }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["uploads", id] });
      // Snapshot previous value
      const previous = queryClient.getQueryData<{ data: MixUpload[] }>(["uploads", id]);
      // Optimistically update (query data is PaginatedResponse, not MixUpload[])
      queryClient.setQueryData<{ data: MixUpload[] }>(["uploads", id], (old) =>
        old ? { ...old, data: old.data.map((u) =>
          u.id === uploadId ? { ...u, moderationStatus: status } : u
        ) } : old
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(["uploads", id], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["uploads", id] });
      queryClient.invalidateQueries({ queryKey: ["event", id] });
      setModeratingId(null);
    },
  });

  const bulkModerateMutation = useMutation({
    mutationFn: ({
      uploadIds,
      status,
    }: {
      uploadIds: string[];
      status: "approved" | "rejected";
    }) => bulkModerate(id!, uploadIds, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["uploads", id] });
      queryClient.invalidateQueries({ queryKey: ["event", id] });
      setSelectedIds(new Set());
    },
  });

  const aiReviewMutation = useMutation({
    mutationFn: () => aiReviewUploads(id!),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["uploads", id] }),
        queryClient.invalidateQueries({ queryKey: ["event", id] }),
      ]);
      // After refetch, check if 0 pending remain — auto-navigate to produce
      const refreshed = queryClient.getQueryData<MixUpload[]>(["uploads", id]);
      const stillPending = refreshed?.filter(
        (u) => u.moderationStatus === "pending"
      );
      if (stillPending && stillPending.length === 0) {
        navigate(`/events/${id}/produce`);
      }
    },
  });

  const [reprocessingIds, setReprocessingIds] = useState<Set<string>>(new Set());
  const [reprocessingAll, setReprocessingAll] = useState(false);

  const reprocessMutation = useMutation({
    mutationFn: (uploadId: string) => reprocessUpload(id!, uploadId),
    onMutate: (uploadId) => {
      setReprocessingIds((prev) => new Set(prev).add(uploadId));
    },
    onSettled: (_data, _err, uploadId) => {
      setReprocessingIds((prev) => {
        const next = new Set(prev);
        next.delete(uploadId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["uploads", id] });
    },
  });

  async function handleReprocessAll() {
    if (!uploads) return;
    const needsReprocess = uploads.filter(
      (u: MixUpload) => !u.width && u.fileStatus !== "processing"
    );
    if (needsReprocess.length === 0) return;
    setReprocessingAll(true);
    for (const upload of needsReprocess) {
      try {
        await reprocessUpload(id!, upload.id);
      } catch {
        // continue with remaining uploads
      }
    }
    setReprocessingAll(false);
    queryClient.invalidateQueries({ queryKey: ["uploads", id] });
  }

  const uploadsNeedingReprocess = uploads?.filter(
    (u: MixUpload) => !u.width && u.fileStatus !== "processing"
  ) ?? [];

  // VIZ-746: Auto-reprocess files missing metadata on page load
  const autoReprocessTriggered = useRef(false);
  useEffect(() => {
    if (autoReprocessTriggered.current) return;
    if (!uploads || uploads.length === 0) return;
    if (uploadsNeedingReprocess.length === 0) return;
    autoReprocessTriggered.current = true;
    handleReprocessAll();
  }, [uploads]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredUploads = uploads?.filter((u: MixUpload) => {
    if (filter === "all") return true;
    return u.moderationStatus === filter;
  });

  const pendingUploads = uploads?.filter(
    (u: MixUpload) => u.moderationStatus === "pending"
  );
  const approvedCount =
    uploads?.filter((u: MixUpload) => u.moderationStatus === "approved").length ?? 0;
  const rejectedCount =
    uploads?.filter((u: MixUpload) => u.moderationStatus === "rejected").length ?? 0;

  function toggleSelect(uploadId: string) {
    const newSet = new Set(selectedIds);
    if (newSet.has(uploadId)) {
      newSet.delete(uploadId);
    } else {
      newSet.add(uploadId);
    }
    setSelectedIds(newSet);
  }

  function handleApproveAll() {
    if (!pendingUploads || pendingUploads.length === 0) return;
    bulkModerateMutation.mutate({
      uploadIds: pendingUploads.map((u: MixUpload) => u.id),
      status: "approved",
    });
  }

  function handleRejectSelected() {
    if (selectedIds.size === 0) return;
    bulkModerateMutation.mutate({
      uploadIds: Array.from(selectedIds),
      status: "rejected",
    });
  }

  const filterTabs: { key: FilterTab; label: string; count: number }[] = [
    { key: "all", label: "All", count: uploads?.length ?? 0 },
    {
      key: "pending",
      label: "Pending",
      count: uploads?.filter((u: MixUpload) => u.moderationStatus === "pending").length ?? 0,
    },
    {
      key: "approved",
      label: "Approved",
      count: uploads?.filter((u: MixUpload) => u.moderationStatus === "approved").length ?? 0,
    },
    {
      key: "rejected",
      label: "Rejected",
      count: uploads?.filter((u: MixUpload) => u.moderationStatus === "rejected").length ?? 0,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/events/${id}`)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-vizimix-100">
            Review Uploads
          </h2>
          <p className="text-sm text-vizimix-400 mt-1">
            {event?.title} -- Moderate participant submissions
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-1 border-b border-organize-border">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={cn(
                "px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
                filter === tab.key
                  ? "border-vizimix-500 text-vizimix-100"
                  : "border-transparent text-vizimix-400 hover:text-vizimix-200"
              )}
            >
              {tab.label}{" "}
              <span className="text-vizimix-500 ml-1">({tab.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {(pendingUploads && pendingUploads.length > 0) ||
      selectedIds.size > 0 ? (
        <div className="flex items-center gap-3 mb-4 p-3 bg-organize-card border border-organize-border rounded-lg">
          {pendingUploads && pendingUploads.length > 0 && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => aiReviewMutation.mutate()}
                disabled={aiReviewMutation.isPending}
                className="gap-2"
              >
                {aiReviewMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {aiReviewMutation.isPending
                  ? "Reviewing..."
                  : `AI Auto Review (${pendingUploads.length})`}
              </Button>
              <Button
                size="sm"
                onClick={handleApproveAll}
                disabled={bulkModerateMutation.isPending}
                className="gap-2"
              >
                {bulkModerateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                Approve All Pending ({pendingUploads.length})
              </Button>
            </>
          )}
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={handleRejectSelected}
              disabled={bulkModerateMutation.isPending}
              className="gap-2"
            >
              {bulkModerateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              Reject Selected ({selectedIds.size})
            </Button>
          )}
        </div>
      ) : null}

      {/* Missing Metadata Banner — auto-reprocesses on load (VIZ-746) */}
      {uploadsNeedingReprocess.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <div className="flex items-center gap-3">
            {reprocessingAll ? (
              <Loader2 className="h-5 w-5 text-yellow-500 animate-spin" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
            )}
            <span className="text-sm font-medium text-yellow-200">
              {reprocessingAll
                ? `Reprocessing ${uploadsNeedingReprocess.length} file(s)...`
                : `${uploadsNeedingReprocess.length} file(s) missing metadata`}
            </span>
          </div>
          {!reprocessingAll && (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleReprocessAll}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Retry
            </Button>
          )}
        </div>
      )}

      {/* Ready to Produce CTA */}
      {pendingUploads &&
        pendingUploads.length === 0 &&
        approvedCount > 0 && (
          <div className="flex items-center justify-between mb-4 p-4 bg-success/10 border border-success/30 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-success" />
              <span className="text-sm font-medium text-success-foreground">
                All uploads reviewed — {approvedCount} approved
                {rejectedCount > 0 && `, ${rejectedCount} rejected`}
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => navigate(`/events/${id}/produce`)}
              className="gap-2"
            >
              Produce Videos
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-vizimix-500" />
        </div>
      )}

      {/* Empty */}
      {filteredUploads && filteredUploads.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Video className="h-12 w-12 text-vizimix-600 mb-4" />
            <h3 className="text-lg font-semibold text-vizimix-200 mb-2">
              No uploads{filter !== "all" ? ` with status "${filter}"` : ""}
            </h3>
          </CardContent>
        </Card>
      )}

      {/* Upload Grid */}
      {filteredUploads && filteredUploads.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredUploads.map((upload: MixUpload) => (
            <UploadCard
              key={upload.id}
              upload={upload}
              selected={selectedIds.has(upload.id)}
              onToggleSelect={() => toggleSelect(upload.id)}
              onExpand={() => {
                const idx = filteredUploads?.indexOf(upload) ?? -1;
                if (idx >= 0) setLightboxIndex(idx);
              }}
              onModerate={(status) => {
                setModeratingId(upload.id);
                moderateMutation.mutate({ uploadId: upload.id, status });
              }}
              isModerating={moderateMutation.isPending && moderatingId === upload.id}
              onReprocess={() => reprocessMutation.mutate(upload.id)}
              isReprocessing={reprocessingIds.has(upload.id)}
            />
          ))}
        </div>
      )}

      {/* Video Lightbox */}
      {lightboxIndex !== null && filteredUploads && filteredUploads[lightboxIndex] && (
        <VideoLightbox
          upload={filteredUploads[lightboxIndex]}
          onClose={() => setLightboxIndex(null)}
          onPrev={lightboxIndex > 0 ? () => setLightboxIndex(lightboxIndex - 1) : undefined}
          onNext={
            lightboxIndex < filteredUploads.length - 1
              ? () => setLightboxIndex(lightboxIndex + 1)
              : undefined
          }
          counter={`${lightboxIndex + 1} / ${filteredUploads.length}`}
          onModerate={
            filteredUploads[lightboxIndex].moderationStatus === "pending"
              ? (status: "approved" | "rejected") => {
                  setModeratingId(filteredUploads[lightboxIndex].id);
                  moderateMutation.mutate({
                    uploadId: filteredUploads[lightboxIndex].id,
                    status,
                  });
                }
              : undefined
          }
          isModerating={
            moderateMutation.isPending &&
            moderatingId === filteredUploads[lightboxIndex].id
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload Card — per-card portrait detection via video/image natural dimensions
// ---------------------------------------------------------------------------

function UploadCard({
  upload,
  selected,
  onToggleSelect,
  onExpand,
  onModerate,
  isModerating,
  onReprocess,
  isReprocessing,
}: {
  upload: MixUpload;
  selected: boolean;
  onToggleSelect: () => void;
  onExpand: () => void;
  onModerate: (status: "approved" | "rejected") => void;
  isModerating: boolean;
  onReprocess: () => void;
  isReprocessing: boolean;
}) {
  const [detectedPortrait, setDetectedPortrait] = useState<boolean | null>(null);

  // DB dimensions take priority if they indicate portrait (post-rotation swap worked)
  const dbPortrait = upload.width && upload.height && upload.height > upload.width;
  const isPortrait = dbPortrait || detectedPortrait;

  function handleVideoMetadata(e: SyntheticEvent<HTMLVideoElement>) {
    const v = e.currentTarget;
    if (v.videoHeight > v.videoWidth) setDetectedPortrait(true);
  }

  function handleImageLoad(e: SyntheticEvent<HTMLImageElement>) {
    const img = e.currentTarget;
    if (img.naturalHeight > img.naturalWidth) setDetectedPortrait(true);
  }

  return (
    <Card className={cn("overflow-hidden", isPortrait && "max-w-52 justify-self-center")}>
      {/* Video / Thumbnail */}
      <div className={cn(
        "relative bg-organize-bg flex items-center justify-center group",
        isPortrait ? "aspect-[9/16] max-h-80" : "aspect-video"
      )}>
        {upload.videoUrl ? (
          <video
            src={upload.videoUrl}
            controls
            preload={upload.thumbnailUrl ? "none" : "metadata"}
            poster={upload.thumbnailUrl}
            className="w-full h-full object-cover"
            onLoadedMetadata={handleVideoMetadata}
          />
        ) : upload.thumbnailUrl ? (
          <img
            src={upload.thumbnailUrl}
            alt="Upload thumbnail"
            className="w-full h-full object-cover"
            onLoad={handleImageLoad}
          />
        ) : (
          <Video className="h-8 w-8 text-vizimix-600" />
        )}
        {/* Checkbox overlay */}
        <label className="absolute top-2 left-2 cursor-pointer">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            className="h-4 w-4 rounded border-organize-border accent-vizimix-600"
          />
        </label>
        {/* Status badge overlay */}
        <div className="absolute top-2 right-2">
          <Badge
            variant={
              upload.moderationStatus === "approved"
                ? "success"
                : upload.moderationStatus === "rejected"
                  ? "destructive"
                  : "secondary"
            }
          >
            {upload.moderationStatus}
          </Badge>
        </div>
        {/* Processing overlay */}
        {upload.fileStatus === "processing" && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin text-white" />
              <span className="text-xs text-white/80">Processing...</span>
            </div>
          </div>
        )}
        {/* Failed processing overlay */}
        {upload.fileStatus === "processing_failed" && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-yellow-500" />
              <span className="text-xs text-yellow-300">Processing failed</span>
            </div>
          </div>
        )}
        {/* Expand button */}
        {(upload.videoUrl || upload.thumbnailUrl) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExpand();
            }}
            className="absolute bottom-2 right-2 p-1.5 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
            aria-label="Expand video"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2 text-sm text-vizimix-200">
          <User className="h-3.5 w-3.5 text-vizimix-500" />
          {upload.participantName || "Unknown"}
        </div>
        <div className="flex items-center gap-3 text-xs text-vizimix-400">
          {upload.durationSeconds && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDuration(upload.durationSeconds)}
            </span>
          )}
          {upload.fileSizeBytes && (
            <span>{formatBytes(upload.fileSizeBytes)}</span>
          )}
        </div>
        <div className="text-xs text-vizimix-500">
          {formatDate(upload.uploadedAt)}
        </div>

        {/* Reprocess button for failed/missing metadata */}
        {!upload.width && upload.fileStatus !== "processing" && (
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-2"
            disabled={isReprocessing}
            onClick={onReprocess}
          >
            {isReprocessing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RotateCcw className="h-3 w-3" />
            )}
            {isReprocessing ? "Reprocessing..." : "Reprocess"}
          </Button>
        )}

        {/* Moderation actions */}
        {upload.moderationStatus === "pending" && (
          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              className="flex-1 gap-1"
              disabled={isModerating}
              onClick={() => onModerate("approved")}
            >
              {isModerating ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Check className="h-3 w-3" />
              )}
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="flex-1 gap-1"
              disabled={isModerating}
              onClick={() => onModerate("rejected")}
            >
              <X className="h-3 w-3" />
              Reject
            </Button>
          </div>
        )}

        {/* AI Review Score */}
        {upload.moderationNote && (() => {
          const match = upload.moderationNote.match(/^AI Review: (\d+(?:\.\d+)?)\/10 — (.+)$/);
          if (!match) return null;
          const score = parseFloat(match[1]);
          const reasoning = match[2];
          const variant = score >= 7 ? "success" : score >= 4 ? "warning" : "destructive";
          return (
            <div className="space-y-1 pt-1">
              <div className="flex items-center gap-2">
                <Sparkles className="h-3 w-3 text-vizimix-400" />
                <Badge variant={variant} className="text-xs">
                  {score}/10
                </Badge>
              </div>
              <p className="text-xs text-vizimix-400 line-clamp-2">
                {reasoning}
              </p>
            </div>
          );
        })()}

        {/* Show note if rejected (non-AI) */}
        {upload.moderationStatus === "rejected" &&
          upload.moderationNote &&
          !upload.moderationNote.startsWith("AI Review:") && (
            <p className="text-xs text-red-400 italic">
              {upload.moderationNote}
            </p>
          )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Video Lightbox
// ---------------------------------------------------------------------------

function VideoLightbox({
  upload,
  onClose,
  onPrev,
  onNext,
  counter,
  onModerate,
  isModerating,
}: {
  upload: MixUpload;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  counter: string;
  onModerate?: (status: "approved" | "rejected") => void;
  isModerating: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-vizimix-300">
              {upload.participantName || "Unknown"}
            </span>
            <Badge
              variant={
                upload.moderationStatus === "approved"
                  ? "success"
                  : upload.moderationStatus === "rejected"
                    ? "destructive"
                    : "secondary"
              }
            >
              {upload.moderationStatus}
            </Badge>
            {upload.durationSeconds && (
              <span className="text-xs text-vizimix-400">
                {formatDuration(upload.durationSeconds)}
              </span>
            )}
            {upload.fileSizeBytes && (
              <span className="text-xs text-vizimix-400">
                {formatBytes(upload.fileSizeBytes)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-vizimix-500">{counter}</span>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md text-vizimix-400 hover:text-vizimix-100 hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Video */}
        <div className="flex-1 min-h-0 px-4">
          {upload.videoUrl ? (
            <video
              key={upload.id}
              src={upload.videoUrl}
              controls
              autoPlay
              poster={upload.thumbnailUrl}
              className="w-full max-h-[70vh] object-contain rounded-lg"
            />
          ) : upload.thumbnailUrl ? (
            <img
              src={upload.thumbnailUrl}
              alt="Upload"
              className="w-full max-h-[70vh] object-contain rounded-lg"
            />
          ) : null}
        </div>

        {/* Bottom bar — moderation + nav */}
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            {onModerate && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="gap-1"
                  disabled={isModerating}
                  onClick={() => onModerate("approved")}
                >
                  {isModerating ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Check className="h-3 w-3" />
                  )}
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="gap-1"
                  disabled={isModerating}
                  onClick={() => onModerate("rejected")}
                >
                  <X className="h-3 w-3" />
                  Reject
                </Button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!onPrev}
              onClick={onPrev}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!onNext}
              onClick={onNext}
              className="gap-1"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
